(() => {
  if (customElements.get('d1-collection-image')) return;

  const requests = new Map();
  const queue = [];
  let active = 0;

  const drain = () => {
    while (active < 4 && queue.length) {
      const run = queue.shift();
      active += 1;
      run().finally(() => {
        active -= 1;
        drain();
      });
    }
  };

  const loadImage = (url) => {
    if (requests.has(url)) return requests.get(url);
    const result = new Promise((resolve, reject) => {
      queue.push(async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 12000);
        try {
          const response = await fetch(url, {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
            signal: controller.signal
          });
          if (!response.ok) throw new Error(`Collection image: ${response.status}`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const image = html.querySelector('[data-d1-collection-icon-image] img');
          resolve(image);
        } catch (error) {
          requests.delete(url);
          reject(error);
        } finally {
          clearTimeout(timeout);
        }
      });
    });
    requests.set(url, result);
    drain();
    return result;
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        observer.unobserve(target);
        target.closest('d1-collection-image')?.load();
      });
    }, { rootMargin: '300px' })
    : null;

  class CollectionImage extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      const image = this.querySelector('img');
      if (!image) return;
      if (observer) observer.observe(image);
      else this.load();
    }

    disconnectedCallback() {
      const image = this.querySelector('img');
      if (observer && image) observer.unobserve(image);
    }

    async load() {
      if (this.started) return;
      this.started = true;
      try {
        const url = new URL(this.dataset.url, window.location.origin);
        if (url.origin !== window.location.origin) return;
        url.search = '';
        url.hash = '';
        url.searchParams.set('section_id', 'd1-collection-icon-image');
        const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const source = await loadImage(url.toString());
        const image = this.querySelector('img');
        if (!source || !image) {
          this.dataset.state = 'empty';
          return;
        }
        ['width', 'height', 'sizes', 'srcset', 'src'].forEach((attribute) => {
          if (source.hasAttribute(attribute)) image.setAttribute(attribute, source.getAttribute(attribute));
        });
        this.dataset.state = 'loaded';
      } catch (error) {
        this.dataset.state = 'error';
      }
    }
  }

  customElements.define('d1-collection-image', CollectionImage);
})();
